import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import {
    useActivateRegistration,
    useRegistrationState,
} from '~/hooks/registration/useRegistrationStore';
import { replanningRunRegistrationsState } from '~/state/atoms/adaptiveReplanningAtoms';
import { useRunScans } from './useRunScans';

export function useSelectRunRegistration(): void {
    const patient = useSelectedPatient();
    const { planning } = useRunScans();
    const runUids = useRecoilValue(replanningRunRegistrationsState);
    const { selectedSopInstanceUid } = useRegistrationState();
    const activateRegistration = useActivateRegistration();

    useEffect(() => {
        if (
            selectedSopInstanceUid ||
            !patient ||
            !planning ||
            runUids.length === 0
        ) {
            return;
        }
        const fromRun = <T extends { sopInstanceUid: string }>(
            candidates: ReadonlyArray<T>
        ) =>
            candidates.find((candidate) =>
                runUids.includes(candidate.sopInstanceUid)
            );
        const target =
            fromRun(patient.deformableRegistrations) ??
            fromRun(patient.registrations);
        if (!target) {
            return;
        }
        // The planning scan is always the run's counterpart; never derive it from whatever is on screen.
        activateRegistration(target.sopInstanceUid, planning.seriesUid);
    }, [
        selectedSopInstanceUid,
        patient,
        runUids,
        planning,
        activateRegistration,
    ]);
}
